import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Lightning, Copy, Tag, TrendUp, TrendDown } from "@phosphor-icons/react";
import { API } from "../lib/api";
import { formatMoney } from "../lib/currency";

export default function SharedStrategy() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    const prev = document.documentElement.className;
    document.documentElement.classList.remove("light");
    document.documentElement.classList.add("dark");
    axios.get(`${API}/strategies/shared/${token}`)
      .then(r => setD(r.data))
      .catch(() => setErr("Report not found or has been revoked."));
    return () => { document.documentElement.className = prev; };
  }, [token]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const positive = useMemo(() => d && d.kpis?.net_pnl >= 0, [d]);
  const accent = positive ? "#10b981" : "#ef4444";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      const el = document.querySelector('[data-testid="ss-copy-btn"] span');
      if (el) { const t = el.textContent; el.textContent = "Copied!"; setTimeout(() => (el.textContent = t), 1200); }
    } catch { /* ignore */ }
  };

  if (err) return <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-neutral-500">{err}</div>;
  if (!d) return <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-neutral-500">Loading…</div>;

  const k = d.kpis;
  const ccy = d.currency || "USD";

  return (
    <div className="min-h-screen bg-neutral-950 text-white grain">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lightning size={22} weight="fill" className="text-primary" />
            <span className="font-display text-xl font-bold">Tradelog</span>
          </div>
          <a href="/" className="text-sm text-neutral-400 hover:text-white">Build your own strategy reports →</a>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 space-y-8">
        {/* Hero */}
        <div
          data-testid="ss-hero-card"
          className="relative rounded-2xl overflow-hidden border border-white/10"
          style={{
            background:
              "radial-gradient(120% 120% at 100% 0%, rgba(16,185,129,0.10) 0%, transparent 50%)," +
              "radial-gradient(120% 120% at 0% 100%, rgba(59,130,246,0.10) 0%, transparent 50%)," +
              "linear-gradient(145deg, #0a0a0a 0%, #050505 100%)",
          }}>
          <div className="absolute inset-0 pointer-events-none opacity-20"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)," +
                "linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
              backgroundSize: "40px 40px",
            }} />
          <div className="relative p-8 sm:p-10 min-h-[380px] flex flex-col justify-between gap-6">
            <div>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-primary/20 border border-primary/40 flex items-center justify-center">
                    <Lightning size={18} weight="fill" className="text-primary" />
                  </div>
                  <div>
                    <p className="font-display text-lg font-bold leading-none">Tradelog</p>
                    <p className="text-[9px] tracking-[0.25em] uppercase text-neutral-400 mt-1">Strategy Report</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-2.5 py-1 rounded bg-primary/15 text-primary text-xs font-semibold flex items-center gap-1">
                    <Tag size={11} weight="fill" /> {d.label}
                  </span>
                  <span data-testid="ss-direction-badge"
                    className={`px-2.5 py-1 rounded font-display text-xs font-black tracking-wide ${positive ? "bg-emerald-500 text-black" : "bg-red-500 text-white"}`}>
                    {positive ? "PROFITABLE" : "UNPROFITABLE"}
                  </span>
                  <span className="px-2.5 py-1 rounded bg-white/10 text-white text-xs font-mono font-semibold">
                    {ccy}
                  </span>
                </div>
              </div>

              <div className="mt-10">
                <p className={`font-display font-black leading-none tracking-tight ${positive ? "text-emerald-400" : "text-red-400"} text-5xl sm:text-6xl`} data-testid="ss-hero-pnl">
                  {formatMoney(k.net_pnl, ccy, { sign: true })}
                </p>
                <p className="text-neutral-400 text-xs mt-3 font-mono uppercase tracking-widest">
                  Net P&amp;L across {k.total_trades} trades · {d.owner_name}
                </p>
              </div>
            </div>

            <div className="flex items-end justify-between gap-6 flex-wrap">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 text-sm">
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">Win Rate</p>
                  <p className="font-mono-num font-bold text-lg" data-testid="ss-winrate">{k.win_rate}%</p>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">Profit Factor</p>
                  <p className="font-mono-num font-bold text-lg" data-testid="ss-pf">{k.profit_factor}</p>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">Expectancy</p>
                  <p className={`font-mono-num font-bold text-lg ${k.expectancy >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {formatMoney(k.expectancy, ccy, { sign: true, short: true })}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">Avg R:R</p>
                  <p className="font-mono-num font-bold text-lg">{k.avg_rr}</p>
                </div>
              </div>
              <div className="p-2 bg-white rounded-lg shadow-2xl shrink-0" data-testid="ss-qr">
                <QRCodeSVG value={shareUrl} size={72} bgColor="#ffffff" fgColor="#000000" level="M" />
              </div>
            </div>
          </div>
        </div>

        <button onClick={copyLink}
          className="h-10 px-4 rounded-full bg-white/5 hover:bg-white/10 text-white text-xs flex items-center gap-2"
          data-testid="ss-copy-btn">
          <Copy size={14} /> <span>Copy link</span>
        </button>

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <p className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">Equity curve</p>
              <p className="font-display text-lg font-bold">{k.first_trade || "—"} → {k.last_trade || "—"}</p>
            </div>
            <div className={`flex items-center gap-1 text-xs font-mono ${positive ? "text-emerald-400" : "text-red-400"}`}>
              {positive ? <TrendUp size={14} /> : <TrendDown size={14} />}
              {positive ? "Positive" : "Negative"} run
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={d.equity_curve}>
                <CartesianGrid stroke="#1a1a1a" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#666" fontSize={11} />
                <YAxis stroke="#666" fontSize={11}
                  tickFormatter={(v) => formatMoney(v, ccy, { short: true })} />
                <Tooltip contentStyle={{ background: "#0e0e0e", border: "1px solid #222", borderRadius: 8 }}
                  formatter={(v) => formatMoney(v, ccy, { sign: true })} />
                <Line type="monotone" dataKey="equity" stroke={accent} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
          <p className="text-[10px] tracking-[0.2em] uppercase text-neutral-500 mb-3">Recent trades in this strategy</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {d.recent_trades.map((t, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg bg-white/[0.03] border border-white/5">
                <div>
                  <p className="text-sm font-medium">{t.symbol}</p>
                  <p className="text-[10px] text-neutral-500">{t.date} · {(t.direction || "").toUpperCase()}</p>
                </div>
                <p className={`font-mono-num font-bold text-sm ${t.pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {formatMoney(t.pnl, ccy, { sign: true })}
                </p>
              </div>
            ))}
          </div>
        </div>

        <footer className="text-center text-xs text-neutral-600 pt-4 pb-8">
          Shared by {d.owner_name} · {(d.created_at || "").slice(0, 10)} · currency {ccy}
        </footer>
      </main>
    </div>
  );
}
